import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { fileRenameInput, folderRenameInput } from '@xdocs/schemas';
import type { FileRenameInput, FolderRenameInput } from '@xdocs/schemas';
import { Button, Input, Modal, cn, useI18n } from '@xdocs/ui';
import { applyWsAlert } from '../../lib/applyWsAlert.js';
import { safeFieldError } from '../../i18n/safeT.js';
import { optionalProp } from '../../lib/optionalProp.js';
import { useExplorerT } from './useExplorerT.js';
import styles from './RenameDialog.module.css';

export interface RenameTarget {
  type: 'file' | 'folder';
  id: string;
  name: string;
}

export interface RenameDialogProps {
  target: RenameTarget | null;
  onClose: () => void;
  onRenameFile: (fileId: string, name: string) => Promise<void>;
  onRenameFolder: (folderId: string, name: string) => Promise<void>;
}

interface RenameFormShellProps {
  title: string;
  onClose: () => void;
  children: ReactElement;
}

function RenameFormShell({ title, onClose, children }: RenameFormShellProps): ReactElement {
  return (
    <Modal open title={title} onClose={onClose} size="sm">
      {children}
    </Modal>
  );
}

/**
 * Shared rename dialog for both explorer files and folders (F7). `file.
 * rename`/`folder.rename` are two DIFFERENT op schemas (`fileRenameInput`
 * keyed by `fileId`, `folderRenameInput` by `folderId`) — RHF's
 * `zodResolver` generic can't type-check against a schema picked at
 * runtime between two incompatible shapes, so this renders one of two
 * small typed sub-forms (still both bound directly to their real op schema,
 * E6 — no re-derived "looser" client copy) rather than forcing a single
 * union-typed form.
 */
export function RenameDialog({ target, onClose, onRenameFile, onRenameFolder }: RenameDialogProps): ReactElement | null {
  if (!target) return null;
  if (target.type === 'folder') {
    return <RenameFolderForm key={target.id} target={target} onClose={onClose} onRenameFolder={onRenameFolder} />;
  }
  return <RenameFileForm key={target.id} target={target} onClose={onClose} onRenameFile={onRenameFile} />;
}

function RenameFileForm({
  target,
  onClose,
  onRenameFile,
}: {
  target: RenameTarget;
  onClose: () => void;
  onRenameFile: (fileId: string, name: string) => Promise<void>;
}): ReactElement {
  const { t: tCommon, locale } = useI18n();
  const { t } = useExplorerT();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FileRenameInput>({
    resolver: zodResolver(fileRenameInput),
    defaultValues: { fileId: target.id, name: target.name },
  });

  async function onSubmit(values: FileRenameInput): Promise<void> {
    try {
      await onRenameFile(values.fileId, values.name);
      onClose();
    } catch (err) {
      applyWsAlert(err, setError, locale);
    }
  }

  return (
    <RenameFormShell title={t('explorer:rename.title')} onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        <Input
          label={t('explorer:rename.label')}
          required
          {...optionalProp('error', safeFieldError(locale, errors.name?.message))}
          {...register('name')}
        />
        <div className={cn(styles['actions'])}>
          <Button variant="secondary" type="button" onClick={onClose} disabled={isSubmitting}>
            {tCommon('common:action.cancel')}
          </Button>
          <Button variant="primary" type="submit" state={isSubmitting ? 'loading' : 'idle'}>
            {t('explorer:action.save')}
          </Button>
        </div>
      </form>
    </RenameFormShell>
  );
}

function RenameFolderForm({
  target,
  onClose,
  onRenameFolder,
}: {
  target: RenameTarget;
  onClose: () => void;
  onRenameFolder: (folderId: string, name: string) => Promise<void>;
}): ReactElement {
  const { t: tCommon, locale } = useI18n();
  const { t } = useExplorerT();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FolderRenameInput>({
    resolver: zodResolver(folderRenameInput),
    defaultValues: { folderId: target.id, name: target.name },
  });

  async function onSubmit(values: FolderRenameInput): Promise<void> {
    try {
      await onRenameFolder(values.folderId, values.name);
      onClose();
    } catch (err) {
      applyWsAlert(err, setError, locale);
    }
  }

  return (
    <RenameFormShell title={t('explorer:rename.title')} onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        <Input
          label={t('explorer:rename.label')}
          required
          {...optionalProp('error', safeFieldError(locale, errors.name?.message))}
          {...register('name')}
        />
        <div className={cn(styles['actions'])}>
          <Button variant="secondary" type="button" onClick={onClose} disabled={isSubmitting}>
            {tCommon('common:action.cancel')}
          </Button>
          <Button variant="primary" type="submit" state={isSubmitting ? 'loading' : 'idle'}>
            {t('explorer:action.save')}
          </Button>
        </div>
      </form>
    </RenameFormShell>
  );
}
